import { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";

const LOGO = "/assets/images/logo-paseo.webp";

const Navigation = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { lang, setLang, t } = useLanguage();

  const navItems = [
    { label: t.nav.project, href: "#discover" },
    { label: t.nav.investment, href: "#investment" },
    { label: t.nav.location, href: "#location" },
    { label: t.nav.development, href: "#development" },
    { label: t.nav.tour, href: "#tour360" },
    { label: t.nav.faq, href: "#faq" },
    { label: t.nav.contact, href: "#contact" },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsVisible(window.scrollY > 300);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleNavClick = (href) => {
    setIsMobileMenuOpen(false);
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <nav
      data-testid="navigation"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isVisible
          ? "bg-[#B5613B] shadow-md py-3 translate-y-0 opacity-100"
          : "-translate-y-full opacity-0"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8 flex items-center justify-between">
        {/* Logo */}
        <a href="#" className="flex-shrink-0" data-testid="nav-logo">
          <img
            src={LOGO}
            alt="Paseo Chahué"
            className="h-8 md:h-10 transition-all duration-300"
          />
        </a>

        {/* Desktop Navigation */}
        <div className="hidden lg:flex items-center gap-6">
          {navItems.map((item, index) => (
            <button
              key={index}
              onClick={() => handleNavClick(item.href)}
              data-testid={`nav-item-${index}`}
              className={`text-sm font-medium tracking-wide transition-colors duration-300 text-white/90 hover:text-white ${item.href === "#contact" ? "bg-[#D4A03D] text-white px-5 py-2 hover:bg-[#c4922d]" : ""}`}
            >
              {item.label}
            </button>
          ))}

          {/* Language Flags */}
          <div className="flex items-center gap-1 ml-2 border-l border-white/30 pl-4">
            <button
              onClick={() => setLang("en")}
              data-testid="lang-en"
              className={`text-lg leading-none px-1 py-0.5 rounded transition-all ${lang === "en" ? "opacity-100 scale-110 bg-white/20" : "opacity-60 hover:opacity-100"}`}
              title="English"
            >
              🇬🇧
            </button>
            <button
              onClick={() => setLang("es")}
              data-testid="lang-es"
              className={`text-lg leading-none px-1 py-0.5 rounded transition-all ${lang === "es" ? "opacity-100 scale-110 bg-white/20" : "opacity-60 hover:opacity-100"}`}
              title="Español"
            >
              🇪🇸
            </button>
          </div>
        </div>

        {/* Mobile Menu Button */}
        <div className="lg:hidden flex items-center gap-3">
          {/* Mobile flags */}
          <div className="flex items-center gap-1">
            <button onClick={() => setLang("en")} className={`text-base ${lang === "en" ? "opacity-100" : "opacity-50"}`}>🇬🇧</button>
            <button onClick={() => setLang("es")} className={`text-base ${lang === "es" ? "opacity-100" : "opacity-50"}`}>🇪🇸</button>
          </div>
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="p-2"
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? (
              <X className="w-6 h-6 text-[#2D2926]" />
            ) : (
              <Menu className="w-6 h-6 text-white" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="lg:hidden bg-white border-t border-[#B5613B]/10 shadow-lg">
          <div className="max-w-7xl mx-auto px-4 py-4 space-y-1">
            {navItems.map((item, index) => (
              <button
                key={index}
                onClick={() => handleNavClick(item.href)}
                data-testid={`mobile-nav-item-${index}`}
                className={`block w-full text-left py-3 px-4 text-[#2D2926] hover:bg-[#FAF8F5] hover:text-[#B5613B] transition-colors ${
                  item.href === "#contact" ? "bg-[#D4A03D] text-white hover:bg-[#c4922d] hover:text-white mt-2" : ""
                }`}
              >
                {item.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navigation;
